import { RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { createFileRoute } from "@tanstack/react-router";

import { AppShell } from "~/components/app-shell";
import { LoadFailed } from "~/components/load-failed";
import { NoAccess } from "~/components/no-access";
import { Splash } from "~/components/splash";
import { errorMessage, isNotStaff, isUnauthenticated } from "~/lib/api";
import { useMe } from "~/lib/queries";

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
});

/**
 * The gate for the entire authenticated subtree.
 *
 * Deliberately *not* meerkat's organization gate: Clerk Organizations are
 * meerkat's tenant boundary, and this project has no orgs -- it has employees.
 * The equivalent step here is the staff check.
 *
 *   1. Clerk still loading            -> splash
 *   2. Not signed in                  -> RedirectToSignIn
 *   3. Signed in, /admin/me pending   -> splash
 *   4. 403 not_staff                  -> the access screen
 *   5. Staff                          -> the shell
 *
 * The server is the real gate: every /admin/* route runs ClerkAuth and
 * RequireStaff independently (internal/api/routes/routes.go). This exists so a
 * non-staff user gets one clear screen rather than a cascade of failed
 * requests behind a half-rendered dashboard.
 */
function AuthedLayout() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return <Splash />;
  if (!isSignedIn) return <RedirectToSignIn />;

  return <StaffGate />;
}

function StaffGate() {
  const { data: me, error, isPending, refetch } = useMe();

  if (isPending) return <Splash />;

  // 403 not_staff is the expected rejection and gets the explanatory screen.
  if (isNotStaff(error)) return <NoAccess />;

  // A 401 means the session died between Clerk loading and this call, so
  // sign-in is genuinely the right destination.
  if (isUnauthenticated(error)) return <RedirectToSignIn />;

  // Anything else -- 5xx, or the API being down -- is NOT an auth problem.
  // Redirecting here would loop: the user is signed in, so Clerk would send
  // them straight back. Say what went wrong and offer a retry instead.
  if (error) return <LoadFailed message={errorMessage(error)} onRetry={() => void refetch()} />;

  // Belt and braces: RequireStaff never lets a false is_staff through, so this
  // is unreachable via the mounted chain -- but failing closed is free.
  if (!me?.is_staff) return <NoAccess email={me?.email} />;

  return <AppShell />;
}
